import { useEffect, useCallback } from 'react';
import { showRemoteCommandResponseSnack } from '@/utils/snackbarUtils';
import { useEnqueueSnackbar } from '@/utils/customHooks/useEnqueueSnackbar';
import { isEmpty, isNil } from 'ramda';
import { ERROR_CODES } from '@/utils/constants';

interface ErrorInfo {
  code?: keyof typeof ERROR_CODES;
  message?: string;
}

interface ErrorBoundaryProps {
  error?: ErrorInfo | null;
  successInfo?: ErrorInfo | null;
  closeError: () => void;
  closeSuccessInfo?: () => void;
}

const getErrorType = (code?: keyof typeof ERROR_CODES) => {
  switch (code) {
    case ERROR_CODES.ERROR:
      return 'error';
    case ERROR_CODES.WARNING:
      return 'warning';
    case ERROR_CODES.SUCCESS:
      return 'success';
    default:
      return 'info';
  }
};

const ErrorBoundary: React.FC<ErrorBoundaryProps> = ({ error, closeError, successInfo, closeSuccessInfo }) => {
  const customEnqueueSnackbar = useEnqueueSnackbar();
  

  // Adapter function to convert the expected signature
  const enqueueSnackbarAdapter = useCallback((message: string, options?: object) => {
    customEnqueueSnackbar({ message, options: { variant: 'info', ...options }, onClose: closeError });
  }, [customEnqueueSnackbar, closeError]);

  useEffect(() => {
    if (isNil(error) || isEmpty(error) || !error?.message) return;

    showRemoteCommandResponseSnack({
      variant: getErrorType(error.code),
      message: error.message,
      enqueueSnackbar: enqueueSnackbarAdapter, // Use the adapter function
      onClose: closeError,
    });
  }, [error, customEnqueueSnackbar, closeError, enqueueSnackbarAdapter]);

  useEffect(() => {
    if (isNil(successInfo) || isEmpty(successInfo) || !successInfo?.message) return;

    showRemoteCommandResponseSnack({
      variant: getErrorType(successInfo.code),
      message: successInfo.message,
      enqueueSnackbar: enqueueSnackbarAdapter, // Use the adapter function
      onClose: closeSuccessInfo,
    });
  }, [successInfo, customEnqueueSnackbar, closeSuccessInfo, enqueueSnackbarAdapter]);

  return null; // This component only handles side effects
};

export default ErrorBoundary;